import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Webcam from 'react-webcam';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { StarRating } from '../components/StarRating';
import VoiceNote from '../components/VoiceNote';
import {
  Building2,
  ArrowLeft,
  Save,
  Camera,
  Upload,
  X,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Send,
  Image as ImageIcon,
  MapPin,
  Loader2,
  WifiOff,
  Cloud,
  CloudOff,
  CloudUpload,
  CheckCircle2,
  AlertTriangle,
  HardDrive
} from 'lucide-react';
import { 
  getCurrentLocation, 
  formatCoordinates, 
  compressImage, 
  fileToBase64, 
  offlineStorage,
  uploadPhotoImmediate,
  getStorageEstimate,
  isStorageNearFull,
  migrateDraftPhotos
} from '../utils/mediaUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultCategories = {
  civil_works: 5,
  electric_plumbing: 5,
  tile_works: 5,
  painting_works: 5,
  doors_windows: 5,
  fabrication_work: 5
};

const categoryLabels = {
  civil_works: 'Civil Works',
  electric_plumbing: 'Electric & Plumbing',
  tile_works: 'Tile Works',
  painting_works: 'Painting Works',
  doors_windows: 'Doors & Windows',
  fabrication_work: 'Fabrication Work'
};

export default function InspectionFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, token } = useAuth();
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [currentPhotoTarget, setCurrentPhotoTarget] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [capturingGPS, setCapturingGPS] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [localDraftId, setLocalDraftId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  
  // Auto-save state
  const [serverDraftId, setServerDraftId] = useState(null); // ID of draft on server
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const isFirstRender = useRef(true);
  
  // Photo upload tracking state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current: 1, total: 5, photoType: 'defect' }
  const [uploadedPhotos, setUploadedPhotos] = useState([]); // Track uploaded photo IDs
  const [storageWarning, setStorageWarning] = useState(null); // { percentUsed: 80, message: '...' }
  const [photosToUpload, setPhotosToUpload] = useState([]); // Queue for offline photos to sync
  
  const [formData, setFormData] = useState({
    client_name: '',
    project_name: '',
    property_address: '',
    date_of_inspection: new Date().toISOString().split('T')[0],
    qc_engineer_name: '',
    property_photo: null,      // Property photo for overview page
    view_360_link: '',         // 360° view hyperlink
    overall_rating: 5,
    category_ratings: { ...defaultCategories },
    area_ratings: [],
    area_measurements: [],
    doors_windows: [],
    brand_types: [],
    dimensions: [],
    area_defects: [],
    test_statements: [],
    agreement_comparisons: [],
    conclusion: '',
    custom_fields: [],
    custom_tables: [],
    // Section notes fields
    area_statement_notes: '',
    doors_windows_notes: '',
    brand_type_notes: '',
    dimension_notes: '',
    agreement_comparison_notes: ''
  });

  // Online/offline detection and auto-save
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing your data...');
      // Trigger autosave when back online
      if (hasUnsavedChanges) {
        performAutoSave();
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Changes will be saved locally.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [hasUnsavedChanges]);

  // Real-time autosave - triggers 2 seconds after user stops typing
  useEffect(() => {
    // Skip on first render and when editing existing inspection
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Only autosave for new inspections (not when editing)
    if (id) return;
    
    // Check if form has meaningful data
    const hasData = formData.client_name || formData.project_name || 
                    formData.property_address || formData.conclusion;
    
    if (!hasData) return;
    
    setHasUnsavedChanges(true);
    setAutoSaveStatus('idle');
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer - autosave 2 seconds after last change
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
    }, 2000);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, gpsLocation]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && autoSaveStatus !== 'saved') {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, autoSaveStatus]);

  // Perform autosave to server and local storage
  const performAutoSave = async () => {
    setAutoSaveStatus('saving');
    
    try {
      // Always save to local storage first (fast, works offline)
      await saveLocalDraft();
      
      // Then try to save to server if online
      if (isOnline) {
        await saveToServer();
      }
      
      setAutoSaveStatus('saved');
      setHasUnsavedChanges(false);
      setLastAutoSave(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Autosave failed:', error);
      setAutoSaveStatus('error');
      // Don't show error toast for every autosave failure - just indicate in UI
    }
  };

  // Save draft to server
  const saveToServer = async () => {
    const dataToSave = {
      ...formData,
      gps_location: gpsLocation,
      status: 'draft'
    };
    
    try {
      if (serverDraftId) {
        // Update existing draft
        await axios.put(`${API_URL}/inspections/${serverDraftId}`, dataToSave, {
          headers: getAuthHeaders()
        });
      } else {
        // Create new draft
        const response = await axios.post(`${API_URL}/inspections`, dataToSave, {
          headers: getAuthHeaders()
        });
        setServerDraftId(response.data.id);
        // Update URL without navigation to preserve state
        window.history.replaceState(null, '', `/inspection/${response.data.id}`);
      }
    } catch (error) {
      console.error('Server save failed:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (id) {
      fetchInspection();
      setServerDraftId(id); // Set server draft ID when editing
    } else {
      // Check for unsaved local drafts when starting a new inspection
      checkForUnsavedDrafts();
    }
  }, [id]);
  
  // Check for unsaved local drafts and offer to restore
  const checkForUnsavedDrafts = async () => {
    try {
      const drafts = await offlineStorage.getDrafts();
      const unsyncedDrafts = drafts.filter(d => !d.synced && !d.serverId);
      
      if (unsyncedDrafts.length > 0) {
        const latestDraft = unsyncedDrafts.sort((a, b) => 
          new Date(b.updatedAt) - new Date(a.updatedAt)
        )[0];
        
        // Only offer to restore if draft has meaningful data
        if (latestDraft.client_name || latestDraft.project_name) {
          const shouldRestore = window.confirm(
            `Found an unsaved draft from ${new Date(latestDraft.updatedAt).toLocaleString()}.\n\n` +
            `Client: ${latestDraft.client_name || 'Not set'}\n` +
            `Project: ${latestDraft.project_name || 'Not set'}\n\n` +
            `Would you like to restore it?`
          );
          
          if (shouldRestore) {
            // Restore the draft data
            setFormData({
              ...formData,
              ...latestDraft,
              localId: undefined,
              synced: undefined,
              serverId: undefined,
              createdAt: undefined,
              updatedAt: undefined
            });
            setLocalDraftId(latestDraft.localId);
            if (latestDraft.gps_location) {
              setGpsLocation(latestDraft.gps_location);
            }
            toast.success('Draft restored! Continue where you left off.');
          }
        }
      }
    } catch (error) {
      console.error('Failed to check for drafts:', error);
    }
  };

  // Save draft to local storage
  const saveLocalDraft = async () => {
    try {
      const draftData = {
        ...formData,
        gps_location: gpsLocation,
        localId: localDraftId || `draft_${Date.now()}`
      };
      await offlineStorage.saveDraft(draftData);
      if (!localDraftId) {
        setLocalDraftId(draftData.localId);
      }
      setLastAutoSave(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const fetchInspection = async () => {
    try {
      const response = await axios.get(`${API_URL}/inspections/${id}`, {
        headers: getAuthHeaders()
      });
      setFormData(response.data);
      // Load existing GPS location if available
      if (response.data.gps_location) {
        setGpsLocation(response.data.gps_location);
      }
    } catch (error) {
      toast.error('Failed to load inspection');
      navigate('/dashboard');
    }
  };

  // Capture GPS location
  const captureGPSLocation = async () => {
    setCapturingGPS(true);
    try {
      const location = await getCurrentLocation();
      setGpsLocation(location);
      toast.success('GPS location captured');
    } catch (error) {
      console.error('GPS error:', error);
      if (error.code === 1) {
        toast.error('Location permission denied. Please enable location access.');
      } else if (error.code === 2) {
        toast.error('Location unavailable. Please check your GPS settings.');
      } else if (error.code === 3) {
        toast.error('Location request timed out. Please try again.');
      } else {
        toast.error('Failed to capture location');
      }
    } finally {
      setCapturingGPS(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Include GPS location in the data
      const dataWithGPS = {
        ...formData,
        gps_location: gpsLocation
      };
      
      // If offline, save locally
      if (!isOnline) {
        await saveLocalDraft();
        toast.success('Saved locally (offline). Will sync when online.');
        setSaving(false);
        return;
      }
      
      if (id) {
        await axios.put(`${API_URL}/inspections/${id}`, dataWithGPS, {
          headers: getAuthHeaders()
        });
        toast.success('Inspection saved');
        // Clear local draft if exists
        if (localDraftId) {
          await offlineStorage.deleteDraft(localDraftId);
          setLocalDraftId(null);
        }
      } else {
        const response = await axios.post(`${API_URL}/inspections`, {
          client_name: formData.client_name,
          project_name: formData.project_name,
          property_address: formData.property_address,
          date_of_inspection: formData.date_of_inspection,
          gps_location: gpsLocation
        }, {
          headers: getAuthHeaders()
        });
        // Clear local draft if exists
        if (localDraftId) {
          await offlineStorage.markSynced(localDraftId, response.data.id);
        }
        navigate(`/inspection/${response.data.id}/edit`);
        toast.success('Inspection created');
      }
    } catch (error) {
      toast.error('Failed to save inspection');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!id) return;
    try {
      await axios.post(`${API_URL}/inspections/${id}/submit`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Submitted for review');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to submit');
    }
  };

  const capturePhoto = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc && currentPhotoTarget) {
        // Process camera capture for portrait rotation
        processAndRotateImage(imageSrc);
      }
      setShowCamera(false);
    }
  }, [currentPhotoTarget]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setCompressing(true);
    try {
      // Compress the image first
      const compressedFile = await compressImage(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      });
      
      const originalSize = (file.size / 1024 / 1024).toFixed(2);
      const compressedSize = (compressedFile.size / 1024 / 1024).toFixed(2);
      
      if (originalSize !== compressedSize) {
        toast.success(`Image compressed: ${originalSize}MB → ${compressedSize}MB`);
      }
      
      // Convert to base64
      const base64 = await fileToBase64(compressedFile);
      
      // Check if image is portrait and rotate if needed
      processAndRotateImage(base64);
    } catch (error) {
      console.error('Image processing error:', error);
      // Fallback to original processing
      const reader = new FileReader();
      reader.onload = () => {
        processAndRotateImage(reader.result);
      };
      reader.readAsDataURL(file);
    } finally {
      setCompressing(false);
    }
    e.target.value = '';
  };

  // Function to check orientation and rotate portrait images to landscape
  const processAndRotateImage = (imageData) => {
    const img = new window.Image();
    img.onload = () => {
      // Check if portrait (height > width)
      if (img.height > img.width) {
        // Rotate 90 degrees clockwise to make landscape
        const canvas = document.createElement('canvas');
        // Swap width and height for rotated image
        canvas.width = img.height;
        canvas.height = img.width;
        const ctx = canvas.getContext('2d');
        
        // Rotate 90 degrees clockwise
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(90 * Math.PI / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        
        // Convert back to base64
        const rotatedImage = canvas.toDataURL('image/jpeg', 0.9);
        handlePhotoCapture(rotatedImage);
        toast.info('Portrait image auto-rotated to landscape');
      } else {
        // Already landscape, use as is
        handlePhotoCapture(imageData);
      }
    };
    img.src = imageData;
  };

  const handlePhotoCapture = async (imageData) => {
    if (!currentPhotoTarget) return;
    
    const { type, areaIndex, defectIndex, testIndex, photoNum } = currentPhotoTarget;
    
    // First, update the UI immediately with the base64 for display
    if (type === 'property') {
      setFormData(prev => ({ ...prev, property_photo: imageData }));
    } else if (type === 'defect' && areaIndex !== undefined && defectIndex !== undefined) {
      setFormData(prev => {
        const newAreaDefects = [...prev.area_defects];
        if (photoNum === 2) {
          newAreaDefects[areaIndex].defects[defectIndex].image2 = imageData;
        } else {
          newAreaDefects[areaIndex].defects[defectIndex].image = imageData;
        }
        return { ...prev, area_defects: newAreaDefects };
      });
    } else if (type === 'test' && testIndex !== undefined) {
      setFormData(prev => {
        const newTests = [...prev.test_statements];
        newTests[testIndex].image = imageData;
        return { ...prev, test_statements: newTests };
      });
    }
    
    setCurrentPhotoTarget(null);
    
    // Now upload immediately in the background if online
    if (isOnline && token) {
      uploadPhotoToServer(imageData, type, areaIndex, defectIndex, testIndex, photoNum);
    } else {
      // Queue for later upload when back online
      setPhotosToUpload(prev => [...prev, { 
        imageData, type, areaIndex, defectIndex, testIndex, photoNum,
        queuedAt: new Date().toISOString()
      }]);
      toast.info('Photo saved locally. Will upload when online.', { duration: 2000 });
    }
  };
  
  // Background upload function
  const uploadPhotoToServer = async (imageData, type, areaIndex, defectIndex, testIndex, photoNum) => {
    setUploadingPhoto(true);
    setUploadProgress({ photoType: type, status: 'uploading' });
    
    try {
      // Convert base64 to blob for upload
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      const result = await uploadPhotoImmediate(file, {
        photoType: type,
        inspectionId: id || null,
        draftId: localDraftId,
        token
      });
      
      // Track uploaded photo
      setUploadedPhotos(prev => [...prev, {
        photo_id: result.photo_id,
        type,
        areaIndex,
        defectIndex,
        testIndex,
        photoNum,
        size_bytes: result.size_bytes
      }]);
      
      setUploadProgress({ photoType: type, status: 'success' });
      toast.success('Photo uploaded to server', { duration: 1500 });
      
      // Check storage after upload
      checkStorageStatus();
      
    } catch (error) {
      console.error('Photo upload failed:', error);
      setUploadProgress({ photoType: type, status: 'error' });
      toast.error('Photo upload failed. Saved locally.', { duration: 2000 });
      
      // Add to queue for retry
      setPhotosToUpload(prev => [...prev, { 
        imageData, type, areaIndex, defectIndex, testIndex, photoNum,
        queuedAt: new Date().toISOString()
      }]);
    } finally {
      setUploadingPhoto(false);
      // Clear progress after a delay
      setTimeout(() => setUploadProgress(null), 2000);
    }
  };
  
  // Check browser storage status
  const checkStorageStatus = async () => {
    try {
      const estimate = await getStorageEstimate();
      if (estimate.percentUsed >= 80) {
        setStorageWarning({
          percentUsed: estimate.percentUsed,
          message: 'Browser storage is nearly full. Photos are being uploaded to server to prevent data loss.'
        });
      } else if (estimate.percentUsed >= 60) {
        setStorageWarning({
          percentUsed: estimate.percentUsed,
          message: 'Storage usage: ' + estimate.percentUsed + '%'
        });
      } else {
        setStorageWarning(null);
      }
    } catch (e) {
      console.warn('Could not check storage status');
    }
  };
  
  // Sync queued photos when back online
  const syncQueuedPhotos = async () => {
    if (photosToUpload.length === 0 || !isOnline || !token) return;
    
    toast.info(`Syncing ${photosToUpload.length} queued photos...`);
    
    const queue = [...photosToUpload];
    setPhotosToUpload([]);
    
    for (let i = 0; i < queue.length; i++) {
      const { imageData, type, areaIndex, defectIndex, testIndex, photoNum } = queue[i];
      setUploadProgress({ current: i + 1, total: queue.length, photoType: type, status: 'uploading' });
      
      try {
        await uploadPhotoToServer(imageData, type, areaIndex, defectIndex, testIndex, photoNum);
      } catch (e) {
        // Re-queue failed uploads
        setPhotosToUpload(prev => [...prev, queue[i]]);
      }
    }
    
    setUploadProgress(null);
    toast.success('Photo sync complete!');
  };
  
  // Effect to sync photos when coming back online
  useEffect(() => {
    if (isOnline && photosToUpload.length > 0) {
      const timeout = setTimeout(() => syncQueuedPhotos(), 2000);
      return () => clearTimeout(timeout);
    }
  }, [isOnline, photosToUpload.length]);
  
  // Check storage on mount and periodically
  useEffect(() => {
    checkStorageStatus();
    const interval = setInterval(checkStorageStatus, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const openCamera = (target) => {
    setCurrentPhotoTarget(target);
    setShowCamera(true);
  };

  const openFileDialog = (target) => {
    setCurrentPhotoTarget(target);
    fileInputRef.current?.click();
  };

  // Array helpers
  const addAreaRating = () => {
    const newAreaName = '';
    setFormData({
      ...formData,
      area_ratings: [...formData.area_ratings, { area_name: newAreaName, rating: 5 }],
      // Also add to other sections
      area_measurements: [...formData.area_measurements, { particular: newAreaName, length: null, breadth: null, area: null }],
      doors_windows: [...formData.doors_windows, { particular: newAreaName, width: null, height: null, opening: null }],
      area_defects: [...formData.area_defects, { area_name: newAreaName, defects: [] }]
    });
  };

  // Sync area name changes across all sections
  const updateAreaNameAcrossSections = (index, newName, oldName) => {
    const newAreaRatings = [...formData.area_ratings];
    newAreaRatings[index].area_name = newName;
    
    // Update corresponding entries in other sections
    const newAreaMeasurements = formData.area_measurements.map((item, i) => 
      i === index ? { ...item, particular: newName } : item
    );
    const newDoorsWindows = formData.doors_windows.map((item, i) => 
      i === index ? { ...item, particular: newName } : item
    );
    const newAreaDefects = formData.area_defects.map((item, i) => 
      i === index ? { ...item, area_name: newName } : item
    );
    
    setFormData({
      ...formData,
      area_ratings: newAreaRatings,
      area_measurements: newAreaMeasurements,
      doors_windows: newDoorsWindows,
      area_defects: newAreaDefects
    });
  };

  // Remove area from all sections
  const removeAreaFromAllSections = (index) => {
    setFormData({
      ...formData,
      area_ratings: removeItem(formData.area_ratings, index),
      area_measurements: removeItem(formData.area_measurements, index),
      doors_windows: removeItem(formData.doors_windows, index),
      area_defects: removeItem(formData.area_defects, index)
    });
  };

  const addAreaMeasurement = () => {
    setFormData({
      ...formData,
      area_measurements: [...formData.area_measurements, { particular: '', length: null, breadth: null, area: null }]
    });
  };

  const addDoorWindow = () => {
    setFormData({
      ...formData,
      doors_windows: [...formData.doors_windows, { particular: '', width: null, height: null, opening: null }]
    });
  };

  const addBrandType = () => {
    setFormData({
      ...formData,
      brand_types: [...formData.brand_types, { particular: '', brand: '', type_spec: '' }]
    });
  };

  const addDimension = () => {
    setFormData({
      ...formData,
      dimensions: [...formData.dimensions, { particular: '', dimension: '' }]
    });
  };

  const addAreaDefect = () => {
    setFormData({
      ...formData,
      area_defects: [...formData.area_defects, { area_name: '', defects: [] }]
    });
  };

  const addDefectToArea = (areaIndex) => {
    const newAreaDefects = [...formData.area_defects];
    newAreaDefects[areaIndex].defects.push({ id: Date.now().toString(), defect: '', image: null, image2: null });
    setFormData({ ...formData, area_defects: newAreaDefects });
  };

  const addTestStatement = () => {
    setFormData({
      ...formData,
      test_statements: [...formData.test_statements, { finding: '', image: null }]
    });
  };

  const addAgreementComparison = () => {
    setFormData({
      ...formData,
      agreement_comparisons: [...formData.agreement_comparisons, { particulars: '', as_per_agreement: '', actual_finding: '' }]
    });
  };

  const addCustomField = () => {
    setFormData({
      ...formData,
      custom_fields: [...formData.custom_fields, { field_name: '', field_value: '' }]
    });
  };

  // Custom table helpers
  const addCustomTable = () => {
    setFormData({
      ...formData,
      custom_tables: [...formData.custom_tables, { 
        table_name: '', 
        columns: ['Column 1', 'Column 2'], 
        rows: [['', '']] 
      }]
    });
  };

  const addColumnToTable = (tableIndex) => {
    const newTables = [...formData.custom_tables];
    const colNum = newTables[tableIndex].columns.length + 1;
    newTables[tableIndex].columns.push(`Column ${colNum}`);
    // Add empty cell to each row
    newTables[tableIndex].rows = newTables[tableIndex].rows.map(row => [...row, '']);
    setFormData({ ...formData, custom_tables: newTables });
  };

  const removeColumnFromTable = (tableIndex, colIndex) => {
    const newTables = [...formData.custom_tables];
    if (newTables[tableIndex].columns.length <= 1) return; // Keep at least 1 column
    newTables[tableIndex].columns.splice(colIndex, 1);
    newTables[tableIndex].rows = newTables[tableIndex].rows.map(row => {
      const newRow = [...row];
      newRow.splice(colIndex, 1);
      return newRow;
    });
    setFormData({ ...formData, custom_tables: newTables });
  };

  const addRowToTable = (tableIndex) => {
    const newTables = [...formData.custom_tables];
    const numCols = newTables[tableIndex].columns.length;
    newTables[tableIndex].rows.push(Array(numCols).fill(''));
    setFormData({ ...formData, custom_tables: newTables });
  };

  const removeRowFromTable = (tableIndex, rowIndex) => {
    const newTables = [...formData.custom_tables];
    if (newTables[tableIndex].rows.length <= 1) return; // Keep at least 1 row
    newTables[tableIndex].rows.splice(rowIndex, 1);
    setFormData({ ...formData, custom_tables: newTables });
  };

  const updateTableName = (tableIndex, name) => {
    const newTables = [...formData.custom_tables];
    newTables[tableIndex].table_name = name;
    setFormData({ ...formData, custom_tables: newTables });
  };

  const updateColumnName = (tableIndex, colIndex, name) => {
    const newTables = [...formData.custom_tables];
    newTables[tableIndex].columns[colIndex] = name;
    setFormData({ ...formData, custom_tables: newTables });
  };

  const updateCellValue = (tableIndex, rowIndex, colIndex, value) => {
    const newTables = [...formData.custom_tables];
    newTables[tableIndex].rows[rowIndex][colIndex] = value;
    setFormData({ ...formData, custom_tables: newTables });
  };

  const removeTable = (tableIndex) => {
    setFormData({
      ...formData,
      custom_tables: removeItem(formData.custom_tables, tableIndex)
    });
  };

  const removeItem = (array, index) => {
    return array.filter((_, i) => i !== index);
  };

  // StarRating is now imported from components/StarRating.js

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'ratings', label: 'Ratings' },
    { id: 'measurements', label: 'Measurements' },
    { id: 'defects', label: 'Defects' },
    { id: 'tests', label: 'Tests' },
    { id: 'comparison', label: 'Comparison' },
    { id: 'conclusion', label: 'Conclusion' },
    { id: 'custom', label: 'Custom Fields' }
  ];

  const currentTabIndex = tabs.findIndex(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-cyan-400" />
                <span className="text-lg font-heading font-bold">{id ? 'Edit Inspection' : 'New Inspection'}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Offline/Online Status Indicator */}
              <div className={`px-2 py-1 rounded-full flex items-center gap-1 text-xs ${
                isOnline ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
              }`}>
                {isOnline ? <Cloud className="w-3 h-3" /> : <CloudOff className="w-3 h-3" />}
                <span className="hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span>
              </div>
              
              {/* Auto-save indicator */}
              {lastAutoSave && !id && (
                <span className="text-xs text-slate-400 hidden sm:inline">
                  Auto-saved {lastAutoSave}
                </span>
              )}
              
              {/* Real-time autosave status */}
              {!id && (
                <div className={`px-2 py-1 rounded-full flex items-center gap-1 text-xs ${
                  autoSaveStatus === 'saving' ? 'bg-cyan-500/20 text-cyan-400' :
                  autoSaveStatus === 'saved' ? 'bg-emerald-500/20 text-emerald-400' :
                  autoSaveStatus === 'error' ? 'bg-red-500/20 text-red-400' :
                  hasUnsavedChanges ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'
                }`}>
                  {autoSaveStatus === 'saving' && (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span className="hidden sm:inline">Saving...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <>
                      <CheckCircle2 className="w-3 h-3" />
                      <span className="hidden sm:inline">Saved</span>
                    </>
                  )}
                  {autoSaveStatus === 'error' && (
                    <>
                      <AlertTriangle className="w-3 h-3" />
                      <span className="hidden sm:inline">Save failed</span>
                    </>
                  )}
                  {autoSaveStatus === 'idle' && hasUnsavedChanges && (
                    <>
                      <CloudUpload className="w-3 h-3" />
                      <span className="hidden sm:inline">Unsaved</span>
                    </>
                  )}
                  {autoSaveStatus === 'idle' && !hasUnsavedChanges && serverDraftId && (
                    <>
                      <Cloud className="w-3 h-3" />
                      <span className="hidden sm:inline">All saved</span>
                    </>
                  )}
                </div>
              )}
              
              {/* Compressing indicator */}
              {compressing && (
                <span className="text-xs text-cyan-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Compressing...
                </span>
              )}
              
              {/* Photo uploading indicator */}
              {uploadingPhoto && uploadProgress && (
                <span className="text-xs text-cyan-400 flex items-center gap-1">
                  <CloudUpload className="w-3 h-3 animate-pulse" />
                  <span className="hidden sm:inline">
                    {uploadProgress.status === 'uploading' ? 'Uploading...' : 
                     uploadProgress.status === 'success' ? 'Uploaded!' : 'Failed'}
                  </span>
                </span>
              )}
              
              {/* Queued photos indicator */}
              {photosToUpload.length > 0 && !isOnline && (
                <span className="text-xs text-amber-400 flex items-center gap-1" title="Photos queued for upload">
                  <CloudOff className="w-3 h-3" />
                  <span>{photosToUpload.length} queued</span>
                </span>
              )}
              
              {/* Uploaded photos count */}
              {uploadedPhotos.length > 0 && (
                <span className="text-xs text-emerald-400 flex items-center gap-1" title="Photos uploaded to server">
                  <CheckCircle2 className="w-3 h-3" />
                  <span className="hidden sm:inline">{uploadedPhotos.length} uploaded</span>
                </span>
              )}
              
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saving || compressing}
                className="border-slate-600 text-white hover:bg-slate-800"
                data-testid="save-btn"
              >
                {!isOnline && <CloudOff className="w-4 h-4 mr-2" />}
                {isOnline && <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Saving...' : isOnline ? 'Save' : 'Save Local'}
              </Button>
              {id && formData.status === 'draft' && (
                <Button
                  onClick={handleSubmitForReview}
                  disabled={!isOnline}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50"
                  data-testid="submit-review-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit for Review
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Storage Warning Banner */}
      {storageWarning && storageWarning.percentUsed >= 70 && (
        <div className={`px-4 py-2 text-sm flex items-center gap-2 ${
          storageWarning.percentUsed >= 80 
            ? 'bg-red-500/10 text-red-700 border-b border-red-200' 
            : 'bg-amber-500/10 text-amber-700 border-b border-amber-200'
        }`}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{storageWarning.message}</span>
          <div className="flex items-center gap-1">
            <HardDrive className="w-3 h-3" />
            <span className="font-medium">{storageWarning.percentUsed}%</span>
          </div>
        </div>
      )}
      
      {/* Photo Sync Progress Banner */}
      {uploadProgress && uploadProgress.total && (
        <div className="px-4 py-2 bg-cyan-500/10 text-cyan-700 text-sm flex items-center gap-2 border-b border-cyan-200">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          <span>Syncing photos: {uploadProgress.current} of {uploadProgress.total}</span>
          <div className="flex-1 h-1.5 bg-cyan-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-cyan-500 transition-all duration-300"
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Camera Modal - Optimized for Mobile Landscape */}
      {showCamera && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <div className="bg-white w-full h-full sm:rounded-sm sm:max-w-2xl sm:h-auto sm:max-h-[90vh] flex flex-col">
            {/* Header - Compact for landscape */}
            <div className="p-2 sm:p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <h3 className="font-semibold text-sm sm:text-base">Capture Photo</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCamera(false)}
                className="h-8 w-8 p-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            {/* Camera View - Fills available space */}
            <div className="flex-1 relative overflow-hidden min-h-0">
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="w-full h-full object-cover"
                videoConstraints={{
                  facingMode: { ideal: 'environment' },
                  aspectRatio: { ideal: 16/9 }
                }}
              />
              {/* Floating Capture Button - Always visible in landscape */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3 px-4">
                <Button
                  onClick={capturePhoto}
                  className="bg-slate-900 hover:bg-slate-800 h-12 px-6 rounded-full shadow-lg"
                  data-testid="capture-btn"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  Capture
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCamera(false)}
                  className="h-12 px-6 rounded-full bg-white/90 shadow-lg"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="w-full flex-wrap h-auto gap-1 bg-white border border-slate-200 p-1">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex-1 min-w-[100px] data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-sm"
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Project Information</h3>
              <div className="grid gap-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Client Name *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      placeholder="Enter client name"
                      className="mt-1"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label>Project Name *</Label>
                    <Input
                      value={formData.project_name}
                      onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                      placeholder="Enter project name"
                      className="mt-1"
                      data-testid="project-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Property Address *</Label>
                  <Textarea
                    value={formData.property_address}
                    onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                    placeholder="Enter complete address"
                    className="mt-1"
                    data-testid="address-input"
                  />
                </div>
                <div className="sm:w-1/2">
                  <Label>Date of Inspection *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_inspection}
                    onChange={(e) => setFormData({ ...formData, date_of_inspection: e.target.value })}
                    className="mt-1"
                    data-testid="inspection-date-input"
                  />
                </div>
                <div className="sm:w-1/2">
                  <Label>QC Engineer Name</Label>
                  <Input
                    value={formData.qc_engineer_name || ''}
                    onChange={(e) => setFormData({ ...formData, qc_engineer_name: e.target.value })}
                    placeholder="Enter QC Engineer name"
                    className="mt-1"
                    data-testid="qc-engineer-name-input"
                  />
                </div>
                
                {/* GPS Location Capture */}
                <div className="sm:col-span-2">
                  <Label>GPS Location</Label>
                  <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={captureGPSLocation}
                      disabled={capturingGPS}
                      className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                    >
                      {capturingGPS ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Capturing...
                        </>
                      ) : (
                        <>
                          <MapPin className="w-4 h-4 mr-2" />
                          {gpsLocation ? 'Update Location' : 'Capture Location'}
                        </>
                      )}
                    </Button>
                    {gpsLocation && (
                      <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-sm">
                        <MapPin className="w-4 h-4 text-cyan-500" />
                        <span>{formatCoordinates(gpsLocation.latitude, gpsLocation.longitude)}</span>
                        <span className="text-xs text-slate-400">
                          (±{gpsLocation.accuracy?.toFixed(0) || '?'}m)
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Capture GPS coordinates for the property inspection location
                  </p>
                </div>
              </div>
            </div>
            
            {/* Property Photo & 360° View Section */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Property Visuals</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Property Photo */}
                <div>
                  <Label>Property Photo (for Report Overview)</Label>
                  <p className="text-xs text-amber-600 mb-2">📷 Upload landscape photo. Portrait will be auto-rotated.</p>
                  <div className="mt-2">
                    {formData.property_photo ? (
                      <div className="relative inline-block">
                        <img
                          src={formData.property_photo}
                          alt="Property"
                          className="w-40 h-32 object-cover rounded-sm border-2 border-amber-400"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, property_photo: null })}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCurrentPhotoTarget({ type: 'property' });
                            setShowCamera(true);
                          }}
                        >
                          <Camera className="w-4 h-4 mr-1" /> Camera
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCurrentPhotoTarget({ type: 'property' });
                            fileInputRef.current?.click();
                          }}
                        >
                          <Upload className="w-4 h-4 mr-1" /> Upload
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* 360° View Link */}
                <div>
                  <Label>360° View Link</Label>
                  <p className="text-xs text-slate-500 mb-2">Add a link to 360° virtual tour (optional)</p>
                  <Input
                    value={formData.view_360_link || ''}
                    onChange={(e) => setFormData({ ...formData, view_360_link: e.target.value })}
                    placeholder="https://360view.example.com/property"
                    className="mt-1"
                    data-testid="view-360-link-input"
                  />
                  {formData.view_360_link && (
                    <a 
                      href={formData.view_360_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                    >
                      🔗 Preview 360° View
                    </a>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Ratings Tab */}
          <TabsContent value="ratings" className="space-y-6">
            <div className="bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-lg font-semibold mb-2">Overall Rating</h3>
              <p className="text-xs text-slate-500 mb-4">Auto-calculated as average of category ratings</p>
              <div className="flex items-center gap-4">
                <StarRating
                  value={formData.overall_rating}
                  onChange={() => {}} // Read-only, auto-calculated
                  disabled={true}
                />
                <span className="text-2xl font-bold text-slate-800">{formData.overall_rating}/10</span>
                <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                  {formData.overall_rating >= 8 ? 'Excellent' : 
                   formData.overall_rating >= 6 ? 'Good' : 
                   formData.overall_rating >= 4 ? 'Average' : 'Needs Improvement'}
                </span>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Category Ratings</h3>
              <p className="text-xs text-slate-500 mb-4">Rate each category - Overall rating will be calculated automatically</p>
              <div className="space-y-4">
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <div key={key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2 border-b border-slate-100 last:border-0">
                    <span className="font-medium text-slate-700">{label}</span>
                    <StarRating
                      value={formData.category_ratings[key] || 5}
                      onChange={(val) => {
                        const newCategoryRatings = { ...formData.category_ratings, [key]: val };
                        // Auto-calculate overall rating
                        const values = Object.values(newCategoryRatings);
                        const avg = values.reduce((a, b) => a + b, 0) / values.length;
                        const overallRating = Math.round(avg * 2) / 2; // Round to nearest 0.5
                        setFormData({
                          ...formData,
                          category_ratings: newCategoryRatings,
                          overall_rating: overallRating
                        });
                      }}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Area-wise Ratings</h3>
                <Button variant="outline" size="sm" onClick={addAreaRating} data-testid="add-area-rating-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Area
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Areas added here will automatically appear in Area Statement, Doors & Windows, and Defects sections.
              </p>
              <div className="space-y-4">
                {formData.area_ratings.map((item, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-sm">
                    {/* Mobile: Stack vertically, Desktop: Row */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <Input
                        value={item.area_name}
                        onChange={(e) => updateAreaNameAcrossSections(index, e.target.value, item.area_name)}
                        placeholder="Area name (e.g., Living Room)"
                        className="w-full sm:flex-1 text-base h-11"
                      />
                      <div className="flex items-center justify-between sm:justify-end gap-2">
                        <StarRating
                          value={item.rating}
                          onChange={(val) => {
                            const newItems = [...formData.area_ratings];
                            newItems[index].rating = val;
                            setFormData({ ...formData, area_ratings: newItems });
                          }}
                          size="sm"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAreaFromAllSections(index)}
                          title="Remove from all sections"
                          className="h-9 w-9 p-0"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {formData.area_ratings.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No area ratings added yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Measurements Tab */}
          <TabsContent value="measurements" className="space-y-6">
            {/* Area Statement */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Area Statement</h3>
                <Button variant="outline" size="sm" onClick={addAreaMeasurement} data-testid="add-area-measurement-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add More
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Areas from "Area-wise Ratings" are automatically added. You can edit measurements or add more areas.
              </p>
              
              {/* Mobile-friendly card layout */}
              <div className="space-y-3 sm:hidden">
                {formData.area_measurements.map((item, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-sm border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-slate-500">Room/Area</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, area_measurements: removeItem(formData.area_measurements, index) })}
                        className="h-7 w-7 p-0"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <Input
                      value={item.particular}
                      onChange={(e) => {
                        const newItems = [...formData.area_measurements];
                        newItems[index].particular = e.target.value;
                        setFormData({ ...formData, area_measurements: newItems });
                      }}
                      placeholder="Room name"
                      className="h-11 text-base mb-3"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Length (FT)</label>
                        <Input
                          type="number"
                          value={item.length || ''}
                          onChange={(e) => {
                            const newItems = [...formData.area_measurements];
                            newItems[index].length = parseFloat(e.target.value) || null;
                            newItems[index].area = (newItems[index].length || 0) * (newItems[index].breadth || 0);
                            setFormData({ ...formData, area_measurements: newItems });
                          }}
                          className="h-10 text-base"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Breadth (FT)</label>
                        <Input
                          type="number"
                          value={item.breadth || ''}
                          onChange={(e) => {
                            const newItems = [...formData.area_measurements];
                            newItems[index].breadth = parseFloat(e.target.value) || null;
                            newItems[index].area = (newItems[index].length || 0) * (newItems[index].breadth || 0);
                            setFormData({ ...formData, area_measurements: newItems });
                          }}
                          className="h-10 text-base"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 mb-1 block">Area (SFT)</label>
                        <Input value={item.area || ''} disabled className="h-10 text-base bg-slate-100 font-medium" />
                      </div>
                    </div>
                  </div>
                ))}
                {formData.area_measurements.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No measurements added yet. Add areas in "Ratings" tab first.</p>
                )}
              </div>
              
              {/* Desktop table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left p-2">Particular</th>
                      <th className="text-left p-2 w-24">Length (FT)</th>
                      <th className="text-left p-2 w-24">Breadth (FT)</th>
                      <th className="text-left p-2 w-24">Area (SFT)</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.area_measurements.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2">
                          <Input
                            value={item.particular}
                            onChange={(e) => {
                              const newItems = [...formData.area_measurements];
                              newItems[index].particular = e.target.value;
                              setFormData({ ...formData, area_measurements: newItems });
                            }}
                            placeholder="Room name"
                            className="h-9"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={item.length || ''}
                            onChange={(e) => {
                              const newItems = [...formData.area_measurements];
                              newItems[index].length = parseFloat(e.target.value) || null;
                              newItems[index].area = (newItems[index].length || 0) * (newItems[index].breadth || 0);
                              setFormData({ ...formData, area_measurements: newItems });
                            }}
                            className="h-9"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={item.breadth || ''}
                            onChange={(e) => {
                              const newItems = [...formData.area_measurements];
                              newItems[index].breadth = parseFloat(e.target.value) || null;
                              newItems[index].area = (newItems[index].length || 0) * (newItems[index].breadth || 0);
                              setFormData({ ...formData, area_measurements: newItems });
                            }}
                            className="h-9"
                          />
                        </td>
                        <td className="p-2">
                          <Input value={item.area || ''} disabled className="h-9 bg-slate-50" />
                        </td>
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormData({ ...formData, area_measurements: removeItem(formData.area_measurements, index) })}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {formData.area_measurements.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No measurements added yet. Add areas in "Ratings" tab first.</p>
                )}
              </div>
              {/* Area Statement Notes */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <Label className="text-sm font-medium text-slate-700">Notes (for QC Engineer / Reviewer)</Label>
                <Textarea
                  value={formData.area_statement_notes || ''}
                  onChange={(e) => setFormData({ ...formData, area_statement_notes: e.target.value })}
                  placeholder="Add any notes or observations about the area statement..."
                  rows={3}
                  className="mt-2"
                  data-testid="area-statement-notes"
                />
                {/* Voice Note for Area Statement */}
                <div className="mt-2">
                  <VoiceNote
                    value={formData.area_statement_voice_note}
                    onChange={(audioData) => setFormData({ ...formData, area_statement_voice_note: audioData })}
                    compact={true}
                  />
                </div>
              </div>
            </div>

            {/* Doors & Windows */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Doors & Windows</h3>
                <Button variant="outline" size="sm" onClick={addDoorWindow} data-testid="add-door-window-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add More
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Areas from "Area-wise Ratings" are automatically added. You can edit dimensions or add more entries.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left p-2">Particular</th>
                      <th className="text-left p-2 w-24">Width (FT)</th>
                      <th className="text-left p-2 w-24">Height (FT)</th>
                      <th className="text-left p-2 w-24">Opening (SFT)</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.doors_windows.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2">
                          <Input
                            value={item.particular}
                            onChange={(e) => {
                              const newItems = [...formData.doors_windows];
                              newItems[index].particular = e.target.value;
                              setFormData({ ...formData, doors_windows: newItems });
                            }}
                            placeholder="Door/Window name"
                            className="h-9"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={item.width || ''}
                            onChange={(e) => {
                              const newItems = [...formData.doors_windows];
                              newItems[index].width = parseFloat(e.target.value) || null;
                              newItems[index].opening = (newItems[index].width || 0) * (newItems[index].height || 0);
                              setFormData({ ...formData, doors_windows: newItems });
                            }}
                            className="h-9"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={item.height || ''}
                            onChange={(e) => {
                              const newItems = [...formData.doors_windows];
                              newItems[index].height = parseFloat(e.target.value) || null;
                              newItems[index].opening = (newItems[index].width || 0) * (newItems[index].height || 0);
                              setFormData({ ...formData, doors_windows: newItems });
                            }}
                            className="h-9"
                          />
                        </td>
                        <td className="p-2">
                          <Input value={item.opening || ''} disabled className="h-9 bg-slate-50" />
                        </td>
                        <td className="p-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormData({ ...formData, doors_windows: removeItem(formData.doors_windows, index) })}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {formData.doors_windows.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No doors/windows added yet. Add areas in "Ratings" tab first.</p>
                )}
              </div>
              {/* Doors & Windows Notes */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <Label className="text-sm font-medium text-slate-700">Notes (for QC Engineer / Reviewer)</Label>
                <Textarea
                  value={formData.doors_windows_notes || ''}
                  onChange={(e) => setFormData({ ...formData, doors_windows_notes: e.target.value })}
                  placeholder="Add any notes or observations about doors and windows..."
                  rows={3}
                  className="mt-2"
                  data-testid="doors-windows-notes"
                />
                {/* Voice Note for Doors & Windows */}
                <div className="mt-2">
                  <VoiceNote
                    value={formData.doors_windows_voice_note}
                    onChange={(audioData) => setFormData({ ...formData, doors_windows_voice_note: audioData })}
                    compact={true}
                  />
                </div>
              </div>
            </div>

            {/* Brand & Type */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Brand & Type Statement</h3>
                <Button variant="outline" size="sm" onClick={addBrandType} data-testid="add-brand-type-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-3">
                {formData.brand_types.map((item, index) => (
                  <div key={index} className="grid sm:grid-cols-4 gap-2 p-3 bg-slate-50 rounded-sm">
                    <Input
                      value={item.particular}
                      onChange={(e) => {
                        const newItems = [...formData.brand_types];
                        newItems[index].particular = e.target.value;
                        setFormData({ ...formData, brand_types: newItems });
                      }}
                      placeholder="Item name"
                    />
                    <Input
                      value={item.brand}
                      onChange={(e) => {
                        const newItems = [...formData.brand_types];
                        newItems[index].brand = e.target.value;
                        setFormData({ ...formData, brand_types: newItems });
                      }}
                      placeholder="Brand"
                    />
                    <Input
                      value={item.type_spec}
                      onChange={(e) => {
                        const newItems = [...formData.brand_types];
                        newItems[index].type_spec = e.target.value;
                        setFormData({ ...formData, brand_types: newItems });
                      }}
                      placeholder="Type/Model"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, brand_types: removeItem(formData.brand_types, index) })}
                      className="w-10"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                {formData.brand_types.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No brands/types added yet</p>
                )}
              </div>
              {/* Brand & Type Notes */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <Label className="text-sm font-medium text-slate-700">Notes (for QC Engineer / Reviewer)</Label>
                <Textarea
                  value={formData.brand_type_notes || ''}
                  onChange={(e) => setFormData({ ...formData, brand_type_notes: e.target.value })}
                  placeholder="Add any notes or observations about brands and types..."
                  rows={3}
                  className="mt-2"
                  data-testid="brand-type-notes"
                />
                {/* Voice Note for Brand & Type */}
                <div className="mt-2">
                  <VoiceNote
                    value={formData.brand_type_voice_note}
                    onChange={(audioData) => setFormData({ ...formData, brand_type_voice_note: audioData })}
                    compact={true}
                  />
                </div>
              </div>
            </div>

            {/* Dimensions */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Dimension Statement</h3>
                <Button variant="outline" size="sm" onClick={addDimension} data-testid="add-dimension-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-3">
                {formData.dimensions.map((item, index) => (
                  <div key={index} className="grid sm:grid-cols-3 gap-2 p-3 bg-slate-50 rounded-sm">
                    <Input
                      value={item.particular}
                      onChange={(e) => {
                        const newItems = [...formData.dimensions];
                        newItems[index].particular = e.target.value;
                        setFormData({ ...formData, dimensions: newItems });
                      }}
                      placeholder="Measurement type"
                      className="sm:col-span-1"
                    />
                    <Input
                      value={item.dimension}
                      onChange={(e) => {
                        const newItems = [...formData.dimensions];
                        newItems[index].dimension = e.target.value;
                        setFormData({ ...formData, dimensions: newItems });
                      }}
                      placeholder="Dimension value"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, dimensions: removeItem(formData.dimensions, index) })}
                      className="w-10"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                {formData.dimensions.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No dimensions added yet</p>
                )}
              </div>
              {/* Dimension Notes */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <Label className="text-sm font-medium text-slate-700">Notes (for QC Engineer / Reviewer)</Label>
                <Textarea
                  value={formData.dimension_notes || ''}
                  onChange={(e) => setFormData({ ...formData, dimension_notes: e.target.value })}
                  placeholder="Add any notes or observations about dimensions..."
                  rows={3}
                  className="mt-2"
                  data-testid="dimension-notes"
                />
              </div>
            </div>
          </TabsContent>

          {/* Defects Tab */}
          <TabsContent value="defects" className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Area-wise Defects</h3>
              <Button variant="outline" onClick={addAreaDefect} data-testid="add-area-defect-btn">
                <Plus className="w-4 h-4 mr-1" /> Add More Areas
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Areas from "Area-wise Ratings" are automatically added. Add defects for each area.
            </p>

            {formData.area_defects.map((area, areaIndex) => (
              <div key={areaIndex} className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
                {/* Area Header - Mobile optimized */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                  <Input
                    value={area.area_name}
                    onChange={(e) => {
                      const newAreas = [...formData.area_defects];
                      newAreas[areaIndex].area_name = e.target.value;
                      setFormData({ ...formData, area_defects: newAreas });
                    }}
                    placeholder="Area name (e.g., Living Room, Kitchen)"
                    className="w-full sm:max-w-md font-semibold text-base h-11"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData({ ...formData, area_defects: removeItem(formData.area_defects, areaIndex) })}
                    className="self-end sm:self-auto"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>

                <div className="space-y-4">
                  {area.defects.map((defect, defectIndex) => (
                    <div key={defect.id} className="p-3 sm:p-4 bg-slate-50 rounded-sm">
                      {/* Defect Description - Mobile optimized */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                          <Textarea
                            value={defect.defect}
                            onChange={(e) => {
                              const newAreas = [...formData.area_defects];
                              newAreas[areaIndex].defects[defectIndex].defect = e.target.value;
                              setFormData({ ...formData, area_defects: newAreas });
                            }}
                            placeholder="Describe the defect..."
                            rows={3}
                            className="text-base"
                          />
                          
                          {/* Voice Note for Defect */}
                          <div className="mt-2">
                            <VoiceNote
                              value={defect.voice_note}
                              onChange={(audioData) => {
                                const newAreas = [...formData.area_defects];
                                newAreas[areaIndex].defects[defectIndex].voice_note = audioData;
                                setFormData({ ...formData, area_defects: newAreas });
                              }}
                              compact={true}
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newAreas = [...formData.area_defects];
                            newAreas[areaIndex].defects = removeItem(newAreas[areaIndex].defects, defectIndex);
                            setFormData({ ...formData, area_defects: newAreas });
                          }}
                          className="self-start h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>

                      <div className="mt-3">
                        <p className="text-xs text-slate-500 mb-1">Photos (up to 2)</p>
                        <p className="text-xs text-amber-600 mb-2">📷 Please upload landscape photos. Portrait photos will be auto-rotated.</p>
                        <div className="flex items-start gap-4 flex-wrap">
                          {/* Photo 1 */}
                          <div className="flex flex-col items-center">
                            <span className="text-xs text-slate-400 mb-1">Photo 1</span>
                            {defect.image ? (
                              <div className="relative">
                                <img
                                  src={defect.image}
                                  alt="Defect Photo 1"
                                  className="w-28 h-20 object-cover rounded-sm border"
                                />
                                <button
                                  onClick={() => {
                                    const newAreas = [...formData.area_defects];
                                    newAreas[areaIndex].defects[defectIndex].image = null;
                                    setFormData({ ...formData, area_defects: newAreas });
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openCamera({ type: 'defect', areaIndex, defectIndex, photoNum: 1 })}
                                  data-testid={`capture-defect-${areaIndex}-${defectIndex}-1`}
                                >
                                  <Camera className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openFileDialog({ type: 'defect', areaIndex, defectIndex, photoNum: 1 })}
                                >
                                  <Upload className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                          
                          {/* Photo 2 */}
                          <div className="flex flex-col items-center">
                            <span className="text-xs text-slate-400 mb-1">Photo 2</span>
                            {defect.image2 ? (
                              <div className="relative">
                                <img
                                  src={defect.image2}
                                  alt="Defect Photo 2"
                                  className="w-28 h-20 object-cover rounded-sm border"
                                />
                                <button
                                  onClick={() => {
                                    const newAreas = [...formData.area_defects];
                                    newAreas[areaIndex].defects[defectIndex].image2 = null;
                                    setFormData({ ...formData, area_defects: newAreas });
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openCamera({ type: 'defect', areaIndex, defectIndex, photoNum: 2 })}
                                  data-testid={`capture-defect-${areaIndex}-${defectIndex}-2`}
                                >
                                  <Camera className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openFileDialog({ type: 'defect', areaIndex, defectIndex, photoNum: 2 })}
                                >
                                  <Upload className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addDefectToArea(areaIndex)}
                  className="mt-4"
                  data-testid={`add-defect-to-area-${areaIndex}`}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Defect
                </Button>
              </div>
            ))}

            {formData.area_defects.length === 0 && (
              <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
                <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No defects documented yet</p>
                <Button variant="outline" onClick={addAreaDefect} className="mt-4">
                  <Plus className="w-4 h-4 mr-1" /> Add Area
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Tests Tab */}
          <TabsContent value="tests" className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Test Statements</h3>
                <Button variant="outline" size="sm" onClick={addTestStatement} data-testid="add-test-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Test
                </Button>
              </div>

              <div className="space-y-4">
                {formData.test_statements.map((test, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-sm">
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Textarea
                          value={test.finding}
                          onChange={(e) => {
                            const newTests = [...formData.test_statements];
                            newTests[index].finding = e.target.value;
                            setFormData({ ...formData, test_statements: newTests });
                          }}
                          placeholder="Test finding (e.g., TDS found 353 in Tap water)"
                          rows={2}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, test_statements: removeItem(formData.test_statements, index) })}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>

                    <div className="mt-3">
                      <p className="text-xs text-amber-600 mb-2">📷 Please upload landscape photos. Portrait photos will be auto-rotated.</p>
                      <div className="flex items-center gap-4">
                      {test.image ? (
                        <div className="relative">
                          <img src={test.image} alt="Test" className="w-32 h-24 object-cover rounded-sm border" />
                          <button
                            onClick={() => {
                              const newTests = [...formData.test_statements];
                              newTests[index].image = null;
                              setFormData({ ...formData, test_statements: newTests });
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCamera({ type: 'test', testIndex: index })}
                          >
                            <Camera className="w-4 h-4 mr-1" /> Camera
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openFileDialog({ type: 'test', testIndex: index })}
                          >
                            <Upload className="w-4 h-4 mr-1" /> Upload
                          </Button>
                        </div>
                      )}
                      </div>
                    </div>
                  </div>
                ))}
                {formData.test_statements.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No test statements added yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Comparison Tab */}
          <TabsContent value="comparison" className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Agreement vs Actual</h3>
                <Button variant="outline" size="sm" onClick={addAgreementComparison} data-testid="add-comparison-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              <div className="space-y-4">
                {formData.agreement_comparisons.map((item, index) => (
                  <div key={index} className="p-4 bg-slate-50 rounded-sm">
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">Particulars</Label>
                        <Input
                          value={item.particulars}
                          onChange={(e) => {
                            const newItems = [...formData.agreement_comparisons];
                            newItems[index].particulars = e.target.value;
                            setFormData({ ...formData, agreement_comparisons: newItems });
                          }}
                          placeholder="Item name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">As Per Agreement</Label>
                        <Input
                          value={item.as_per_agreement}
                          onChange={(e) => {
                            const newItems = [...formData.agreement_comparisons];
                            newItems[index].as_per_agreement = e.target.value;
                            setFormData({ ...formData, agreement_comparisons: newItems });
                          }}
                          placeholder="Agreed specification"
                          className="mt-1"
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500">Actual Finding</Label>
                          <Input
                            value={item.actual_finding}
                            onChange={(e) => {
                              const newItems = [...formData.agreement_comparisons];
                              newItems[index].actual_finding = e.target.value;
                              setFormData({ ...formData, agreement_comparisons: newItems });
                            }}
                            placeholder="What was found"
                            className="mt-1"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({ ...formData, agreement_comparisons: removeItem(formData.agreement_comparisons, index) })}
                          className="mt-6"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {formData.agreement_comparisons.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No comparisons added yet</p>
                )}
              </div>
              {/* Agreement vs Actual Notes */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <Label className="text-sm font-medium text-slate-700">Notes (for QC Engineer / Reviewer)</Label>
                <Textarea
                  value={formData.agreement_comparison_notes || ''}
                  onChange={(e) => setFormData({ ...formData, agreement_comparison_notes: e.target.value })}
                  placeholder="Add any notes or observations about agreement vs actual findings..."
                  rows={3}
                  className="mt-2"
                  data-testid="agreement-comparison-notes"
                />
              </div>
            </div>
          </TabsContent>

          {/* Conclusion Tab */}
          <TabsContent value="conclusion" className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Conclusion Statement</h3>
              <Textarea
                value={formData.conclusion}
                onChange={(e) => setFormData({ ...formData, conclusion: e.target.value })}
                placeholder="Summarize the overall findings, highlight key concerns and recommendations..."
                rows={10}
                className="w-full"
                data-testid="conclusion-input"
              />
              
              {/* Voice Note for Conclusion */}
              <div className="mt-4">
                <VoiceNote
                  value={formData.conclusion_voice_note}
                  onChange={(audioData) => setFormData({ ...formData, conclusion_voice_note: audioData })}
                  label="Conclusion Voice Note"
                />
              </div>
            </div>
          </TabsContent>

          {/* Custom Fields Tab */}
          <TabsContent value="custom" className="space-y-6">
            {/* Simple Key-Value Fields */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Custom Fields</h3>
                <Button variant="outline" size="sm" onClick={addCustomField} data-testid="add-custom-field-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Field
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-3">Add simple key-value pairs for additional information.</p>

              <div className="space-y-4">
                {formData.custom_fields.map((field, index) => (
                  <div key={index} className="grid sm:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-sm">
                    <Input
                      value={field.field_name}
                      onChange={(e) => {
                        const newFields = [...formData.custom_fields];
                        newFields[index].field_name = e.target.value;
                        setFormData({ ...formData, custom_fields: newFields });
                      }}
                      placeholder="Field name"
                    />
                    <Input
                      value={field.field_value}
                      onChange={(e) => {
                        const newFields = [...formData.custom_fields];
                        newFields[index].field_value = e.target.value;
                        setFormData({ ...formData, custom_fields: newFields });
                      }}
                      placeholder="Field value"
                      className="sm:col-span-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, custom_fields: removeItem(formData.custom_fields, index) })}
                      className="w-10"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                {formData.custom_fields.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No custom fields added yet</p>
                )}
              </div>
            </div>

            {/* Custom Tables */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Custom Tables</h3>
                <Button variant="outline" size="sm" onClick={addCustomTable} data-testid="add-custom-table-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Table
                </Button>
              </div>
              <p className="text-xs text-slate-500 mb-3">Create custom tables with dynamic columns and rows.</p>

              <div className="space-y-6">
                {formData.custom_tables.map((table, tableIndex) => (
                  <div key={tableIndex} className="border border-slate-200 rounded-sm p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-4">
                      <Input
                        value={table.table_name}
                        onChange={(e) => updateTableName(tableIndex, e.target.value)}
                        placeholder="Table name (e.g., Material List)"
                        className="max-w-xs font-semibold"
                      />
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => addColumnToTable(tableIndex)}>
                          <Plus className="w-3 h-3 mr-1" /> Column
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => addRowToTable(tableIndex)}>
                          <Plus className="w-3 h-3 mr-1" /> Row
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeTable(tableIndex)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr>
                            {table.columns.map((col, colIndex) => (
                              <th key={colIndex} className="border border-slate-300 p-1 bg-slate-100">
                                <div className="flex items-center gap-1">
                                  <Input
                                    value={col}
                                    onChange={(e) => updateColumnName(tableIndex, colIndex, e.target.value)}
                                    className="h-8 text-sm font-semibold"
                                    placeholder="Column name"
                                  />
                                  {table.columns.length > 1 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeColumnFromTable(tableIndex, colIndex)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <X className="w-3 h-3 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              </th>
                            ))}
                            <th className="w-10 border border-slate-300 bg-slate-100"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {table.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {row.map((cell, colIndex) => (
                                <td key={colIndex} className="border border-slate-300 p-1">
                                  <Input
                                    value={cell}
                                    onChange={(e) => updateCellValue(tableIndex, rowIndex, colIndex, e.target.value)}
                                    className="h-8 text-sm"
                                    placeholder="Enter value"
                                  />
                                </td>
                              ))}
                              <td className="border border-slate-300 p-1 text-center">
                                {table.rows.length > 1 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeRowFromTable(tableIndex, rowIndex)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Trash2 className="w-3 h-3 text-red-500" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                {formData.custom_tables.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No custom tables added yet</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setActiveTab(tabs[Math.max(0, currentTabIndex - 1)].id)}
            disabled={currentTabIndex === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <Button
            onClick={() => {
              if (currentTabIndex === tabs.length - 1) {
                handleSave();
              } else {
                setActiveTab(tabs[currentTabIndex + 1].id);
              }
            }}
            className="bg-slate-900 hover:bg-slate-800"
          >
            {currentTabIndex === tabs.length - 1 ? (
              <>Save & Finish</>
            ) : (
              <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
